import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { usePerfil } from '../context/PerfilContext';
import { Plus, Trash2, Edit2, Settings, Layers, Pencil, User, Check, Download, Database, ShieldCheck, LayoutTemplate } from 'lucide-react';
import { ModalAgregarCategoria } from '../components/ModalAgregarCategoria';
import { ModalPlantillaCategorias } from '../components/ModalPlantillaCategorias';
import { ModalAgregarCuenta } from '../components/ModalAgregarCuenta';
import { ModalEditarCuenta } from '../components/ModalEditarCuenta';
import { Categoria, Cuenta } from '../types';
import { PanelUsuarios } from '../components/admin/PanelUsuarios';
import { MetodosAcceso } from '../components/perfil/MetodosAcceso';
import { ModalEliminarCuenta } from '../components/perfil/ModalEliminarCuenta';
import { BloqueoBiometrico } from '../components/perfil/BloqueoBiometrico';
import { Apariencia } from '../components/perfil/Apariencia';
import { NotificacionesPush } from '../components/perfil/NotificacionesPush';
import { RecordatoriosGastosAjustes } from '../components/perfil/RecordatoriosGastosAjustes';
import { Vibraciones } from '../components/perfil/Vibraciones';
import { guardarArchivo } from '../utils/plataforma';
import { useConfirmar } from '../context/ConfirmarContext';
import { usePlus } from '../context/PlusContext';
import { useLimite } from '../hooks/useLimite';
import { ContadorLimite } from '../components/plus/ContadorLimite';
import { PlanPlus } from '../components/perfil/PlanPlus';
import { AjustesPlus } from '../components/admin/AjustesPlus';

type Tab = 'perfil' | 'cuentas' | 'categorias' | 'datos' | 'usuarios';
type FiltroCat = 'todos' | 'ingresos' | 'gastos';

const TIPO_LABEL: Record<string, string> = {
  corriente: 'Corriente',
  ahorro: 'Ahorro',
  tarjeta: 'Tarjeta',
  hucha: 'Hucha',
};

export const Configuracion: React.FC = () => {
  const confirmar = useConfirmar();
  const {
    categorias, eliminarCategoria,
    cuentas, eliminarCuenta,
    transacciones, traspasos, presupuestos,
    inversiones, aportacionesInversion, retirosInversion, valoracionesInversion,
    traspasosRecurrentes, transaccionesRecurrentes,
    loading,
  } = useData();
  const { user, updateDisplayName } = useAuth();
  const { esAdmin, pendientesDeActivar, tieneModulo } = usePerfil();
  // Cuentas, categorías y la exportación son de Finanzas: quien no lo tiene
  // (p. ej. alguien que entró por una invitación de A medias) solo ve su perfil.
  const conFinanzas = tieneModulo('finanzas');
  const [tab, setTab] = useState<Tab>('perfil');
  const { ofrecerPlus } = usePlus();
  const limCuentas = useLimite('cuentas');
  const limHuchas = useLimite('huchas');
  const limCategorias = useLimite('categorias');

  // — Perfil —
  const [nombreInput, setNombreInput]   = useState(user?.displayName || '');
  const [guardandoNombre, setGuardandoNombre] = useState(false);
  const [nombreGuardado, setNombreGuardado]   = useState(false);
  const [errorNombre, setErrorNombre]         = useState('');
  const [showEliminarCuenta, setShowEliminarCuenta] = useState(false);

  const handleGuardarNombre = async () => {
    if (!nombreInput.trim()) { setErrorNombre('El nombre no puede estar vacío'); return; }
    setGuardandoNombre(true); setErrorNombre('');
    try {
      await updateDisplayName(nombreInput.trim());
      setNombreGuardado(true);
      setTimeout(() => setNombreGuardado(false), 2500);
    } catch (e: any) {
      setErrorNombre(e.message || 'Error al guardar el nombre');
    } finally {
      setGuardandoNombre(false);
    }
  };

  // — Categorías —
  const [showModalCat, setShowModalCat] = useState(false);
  const [categoriaEditar, setCategoriaEditar] = useState<Categoria | undefined>(undefined);
  const [filtro, setFiltro] = useState<FiltroCat>('todos');
  const [showPlantilla, setShowPlantilla] = useState(false);

  // — Cuentas —
  const [showModalNuevaCuenta, setShowModalNuevaCuenta] = useState(false);
  const [cuentaEditar, setCuentaEditar] = useState<Cuenta | null>(null);

  // — Exportar datos —
  const handleExportar = () => {
    const datos = {
      exportadoEn: new Date().toISOString(),
      cuentas: cuentas.map(({ id, nombre, tipo, icono, saldo, saldoInicial, objetivo, ocultarSaldo, esCredito }) => ({
        id, nombre, tipo, icono, saldo, saldoInicial, objetivo, ocultarSaldo, esCredito,
      })),
      categorias: categorias.map(({ id, nombre, color, icono, tipo }) => ({
        id, nombre, color, icono, tipo,
      })),
      transacciones: transacciones.map(t => ({
        id: t.id, tipo: t.tipo, cantidad: t.cantidad, categoriaId: t.categoriaId,
        cuentaId: t.cuentaId, descripcion: t.descripcion,
        fecha: t.fecha instanceof Date ? t.fecha.toISOString() : t.fecha,
        traspasoId: t.traspasoId, presupuestoId: t.presupuestoId, adelanto: t.adelanto, persona: t.persona, saldado: t.saldado,
      })),
      traspasos: traspasos.map(t => ({
        id: t.id, cuentaOrigenId: t.cuentaOrigenId, cuentaDestinoId: t.cuentaDestinoId,
        inversionDestinoId: t.inversionDestinoId, inversionOrigenId: t.inversionOrigenId,
        cantidad: t.cantidad, descripcion: t.descripcion,
        fecha: t.fecha instanceof Date ? t.fecha.toISOString() : t.fecha,
        categoriaOrigenId: t.categoriaOrigenId, presupuestoOrigenId: t.presupuestoOrigenId,
      })),
      presupuestos: presupuestos.map(p => ({
        id: p.id, nombre: p.nombre, limite: p.limite, categoriaId: p.categoriaId,
        mes: p.mes, periodicidad: p.periodicidad, repetitivo: p.repetitivo, gastado: p.gastado,
        limitesMes: p.limitesMes, historialLimite: p.historialLimite,
      })),
      inversiones: inversiones.map(i => ({
        id: i.id, nombre: i.nombre, tipo: i.tipo, broker: i.broker, ticker: i.ticker,
        fechaInicio: i.fechaInicio instanceof Date ? i.fechaInicio.toISOString() : i.fechaInicio,
        moneda: i.moneda, activo: i.activo, notas: i.notas, comision: i.comision, valorActual: i.valorActual,
      })),
      aportacionesInversion: aportacionesInversion.map(a => ({
        id: a.id, inversionId: a.inversionId,
        fecha: a.fecha instanceof Date ? a.fecha.toISOString() : a.fecha,
        cantidadAportada: a.cantidadAportada, porcentajeComision: a.porcentajeComision,
        comisionAplicada: a.comisionAplicada, cantidadInvertida: a.cantidadInvertida,
        cuentaOrigenId: a.cuentaOrigenId, traspasoRefId: a.traspasoRefId, descripcion: a.descripcion,
      })),
      retirosInversion: retirosInversion.map(r => ({
        id: r.id, inversionId: r.inversionId,
        fecha: r.fecha instanceof Date ? r.fecha.toISOString() : r.fecha,
        cantidadRetirada: r.cantidadRetirada, cuentaDestinoId: r.cuentaDestinoId,
        traspasoRefId: r.traspasoRefId, descripcion: r.descripcion,
      })),
      valoracionesInversion: valoracionesInversion.map(v => ({
        id: v.id, inversionId: v.inversionId,
        fecha: v.fecha instanceof Date ? v.fecha.toISOString() : v.fecha,
        valorTotal: v.valorTotal,
      })),
      traspasosRecurrentes: traspasosRecurrentes.map(t => ({
        id: t.id, nombre: t.nombre, cuentaOrigenId: t.cuentaOrigenId, cuentaDestinoId: t.cuentaDestinoId,
        inversionDestinoId: t.inversionDestinoId, cantidad: t.cantidad, descripcion: t.descripcion,
        periodicidad: t.periodicidad,
        proximaFecha: t.proximaFecha instanceof Date ? t.proximaFecha.toISOString() : t.proximaFecha,
        fechaFin: t.fechaFin instanceof Date ? t.fechaFin.toISOString() : t.fechaFin,
        activo: t.activo,
      })),
      transaccionesRecurrentes: transaccionesRecurrentes.map(t => ({
        id: t.id, nombre: t.nombre, tipo: t.tipo, cantidad: t.cantidad,
        categoriaId: t.categoriaId, cuentaId: t.cuentaId, descripcion: t.descripcion,
        periodicidad: t.periodicidad,
        proximaFecha: t.proximaFecha instanceof Date ? t.proximaFecha.toISOString() : t.proximaFecha,
        fechaFin: t.fechaFin instanceof Date ? t.fechaFin.toISOString() : t.fechaFin,
        activo: t.activo, presupuestoId: t.presupuestoId,
      })),
    };

    const blob = new Blob([JSON.stringify(datos, null, 2)], { type: 'application/json' });
    guardarArchivo(`gastos-export-${new Date().toISOString().slice(0, 10)}.json`, blob)
      .catch(e => console.error('No se pudo exportar', e));
  };

  const handleEliminarCategoria = async (id: string) => {
    if (await confirmar({ titulo: '¿Eliminar esta categoría?', accion: 'Eliminar' })) {
      try { await eliminarCategoria(id); } catch (e) { console.error(e); }
    }
  };

  const handleEliminarCuenta = async (id: string) => {
    if (await confirmar({
      titulo: '¿Eliminar esta cuenta?',
      mensaje: 'Las transacciones asociadas no se eliminarán.',
      accion: 'Eliminar',
    })) {
      try { await eliminarCuenta(id); } catch (e) { console.error(e); }
    }
  };

  const categoriasFiltradas = categorias.filter(c => {
    if (filtro === 'ingresos') return c.tipo === 'ingreso';
    if (filtro === 'gastos') return c.tipo === 'gasto';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-gray-600 dark:text-gray-400">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">

        {/* Cabecera */}
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
            <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configuración</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white dark:bg-gray-800 rounded-xl shadow p-1.5">
          {([
            { key: 'perfil',      label: 'Perfil',      Icon: User   },
            ...(conFinanzas ? [
              { key: 'cuentas' as Tab,     label: 'Cuentas',     Icon: Layers },
              { key: 'categorias' as Tab,  label: 'Categorías',  Icon: Layers },
              { key: 'datos' as Tab,       label: 'Datos',       Icon: Database },
            ] : []),
            // Solo la ve quien administra; las reglas de Firestore son las que
            // impiden de verdad tocar los permisos de nadie.
            ...(esAdmin
              ? [{ key: 'usuarios' as Tab, label: 'Usuarios', Icon: ShieldCheck }]
              : []),
          ] as { key: Tab; label: string; Icon: React.FC<{ className?: string }> }[]).map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`relative flex-1 min-w-0 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5
                          px-0.5 py-2 sm:py-2.5 rounded-lg font-semibold text-[11px] leading-tight sm:text-sm transition ${
                tab === key
                  ? 'bg-blue-600 text-white shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}>
              {/* En el móvil, icono encima y texto debajo: con todas las
                  pestañas (admin) no caben en una sola línea. */}
              <Icon className="w-5 h-5 sm:w-4 sm:h-4 shrink-0" />
              <span className="max-w-full truncate">{label}</span>
              {key === 'usuarios' && pendientesDeActivar > 0 && (
                <span className={`absolute top-0.5 right-0.5 sm:static min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold
                                  flex items-center justify-center ${
                  tab === key ? 'bg-white text-blue-600' : 'bg-amber-500 text-white'
                }`}>
                  {pendientesDeActivar}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── TAB: USUARIOS (administradores) ── */}
        {tab === 'usuarios' && esAdmin && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
              <AjustesPlus />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
              <PanelUsuarios />
            </div>
          </div>
        )}

        {/* ── TAB: PERFIL ── */}
        {tab === 'perfil' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 space-y-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full">
                <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900 dark:text-white">Datos de perfil</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Nombre que aparece en el Dashboard
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={nombreInput}
                  onChange={e => setNombreInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleGuardarNombre()}
                  placeholder="Tu nombre…"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                             focus:outline-none focus:ring-2 focus:ring-blue-500
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
                <button
                  onClick={handleGuardarNombre}
                  disabled={guardandoNombre}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-semibold text-sm transition disabled:opacity-50 ${
                    nombreGuardado
                      ? 'bg-green-600 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}>
                  {nombreGuardado
                    ? <><Check className="w-4 h-4" /> Guardado</>
                    : guardandoNombre ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
              {errorNombre && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errorNombre}</p>
              )}
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                El saludo del Dashboard mostrará este nombre.
              </p>
            </div>

            <PlanPlus />

            <div className="border-t border-gray-100 dark:border-gray-700 pt-5">
              <MetodosAcceso />
            </div>

            <Apariencia />
            <BloqueoBiometrico />
            <NotificacionesPush />
            <RecordatoriosGastosAjustes />
            <Vibraciones />

            <div className="border-t border-gray-100 dark:border-gray-700 pt-5">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Eliminar cuenta</h3>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                Borra tu cuenta y todos tus datos para siempre.{' '}
                <a href="/privacidad" className="underline hover:text-gray-600 dark:hover:text-gray-300">
                  Cómo tratamos tus datos
                </a>
              </p>
              <button onClick={() => setShowEliminarCuenta(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-red-300 dark:border-red-800
                           text-red-600 dark:text-red-400 text-sm font-semibold
                           hover:bg-red-50 dark:hover:bg-red-900/30 transition">
                <Trash2 className="w-4 h-4" /> Eliminar mi cuenta
              </button>
            </div>
          </div>
        )}

        {showEliminarCuenta && <ModalEliminarCuenta onClose={() => setShowEliminarCuenta(false)} />}

        {/* ── TAB: CUENTAS ── */}
        {tab === 'cuentas' && conFinanzas && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {cuentas.length} cuenta{cuentas.length !== 1 ? 's' : ''}
                {limCuentas.texto && ` · plan gratis: ${limCuentas.texto} cuentas y ${limHuchas.texto} huchas`}
              </p>
              <button
                // Sin hueco ni para cuentas ni para huchas, la hoja de Plus; si
                // queda alguno, el modal (y DataContext frena el tipo lleno).
                onClick={() => limCuentas.alcanzado && limHuchas.alcanzado
                  ? ofrecerPlus('cuentas')
                  : setShowModalNuevaCuenta(true)}
                className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition text-sm"
              >
                <Plus className="w-4 h-4" />
                Nueva Cuenta
              </button>
            </div>

            <div className="space-y-3">
              {cuentas.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-8 text-center text-gray-500 dark:text-gray-400">
                  No tienes cuentas. Crea una para comenzar.
                </div>
              ) : (
                cuentas.map(cuenta => (
                  <div
                    key={cuenta.id}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {cuenta.icono && (
                        <span className="text-2xl shrink-0">{cuenta.icono}</span>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                          {cuenta.nombre}
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {TIPO_LABEL[cuenta.tipo] ?? cuenta.tipo}
                          </span>
                          {cuenta.ocultarSaldo && (
                            <span className="text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 px-1.5 py-0.5 rounded-full">
                              Oculto en saldo
                            </span>
                          )}
                          {cuenta.objetivo && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              Objetivo: €{cuenta.objetivo.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!cuenta.ocultarSaldo && (
                        <span className="font-bold text-gray-900 dark:text-white text-sm hidden sm:block">
                          €{cuenta.saldo.toFixed(2)}
                        </span>
                      )}
                      <button
                        onClick={() => setCuentaEditar(cuenta)}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEliminarCuenta(cuenta.id)}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── TAB: CATEGORÍAS ── */}
        {tab === 'categorias' && conFinanzas && (
          <div>
            {/* Controles */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex gap-2 flex-wrap">
                {([
                  { key: 'todos', label: 'Todas', cls: 'bg-blue-600 text-white' },
                  { key: 'ingresos', label: 'Ingresos', cls: 'bg-green-600 text-white' },
                  { key: 'gastos', label: 'Gastos', cls: 'bg-red-600 text-white' },
                ] as const).map(({ key, label, cls }) => (
                  <button
                    key={key}
                    onClick={() => setFiltro(key)}
                    className={`px-3 py-1.5 rounded-lg font-semibold text-sm transition ${
                      filtro === key
                        ? cls
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => limCategorias.intentar(() => setShowPlantilla(true))}
                  className="flex items-center gap-2 border border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-semibold py-2 px-4 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition text-sm"
                >
                  <LayoutTemplate className="w-4 h-4" />
                  Desde plantilla
                </button>
                <button
                  onClick={() => limCategorias.intentar(() => { setCategoriaEditar(undefined); setShowModalCat(true); })}
                  className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Nueva Categoría <ContadorLimite texto={limCategorias.texto} />
                </button>
              </div>
            </div>

            {/* Usuario recién dado de alta: ofrecer la plantilla */}
            {categorias.length === 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5 mb-4 text-center">
                <LayoutTemplate className="w-8 h-8 mx-auto text-blue-600 dark:text-blue-400 mb-2" />
                <p className="font-semibold text-gray-900 dark:text-white">Todavía no tienes categorías</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-md mx-auto">
                  Empieza con las más habituales (vivienda, supermercado, nómina…) y elige cuáles quieres,
                  o créalas una a una a tu gusto.
                </p>
                <button
                  onClick={() => setShowPlantilla(true)}
                  className="inline-flex items-center gap-2 mt-3 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition text-sm"
                >
                  <LayoutTemplate className="w-4 h-4" />
                  Elegir de la plantilla
                </button>
              </div>
            )}

            {/* Grid de categorías */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {categorias.length === 0 ? null : categoriasFiltradas.length === 0 ? (
                <div className="col-span-full bg-white dark:bg-gray-800 rounded-xl shadow p-8 text-center text-gray-500 dark:text-gray-400">
                  No hay categorías
                </div>
              ) : (
                categoriasFiltradas.map(categoria => (
                  <div
                    key={categoria.id}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow p-4"
                    style={{ borderLeft: `4px solid ${categoria.color}` }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-2xl shrink-0">{categoria.icono}</span>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-white truncate text-sm">
                            {categoria.nombre}
                          </h3>
                          <p className={`text-xs font-medium ${
                            categoria.tipo === 'ingreso' ? 'text-green-600' : 'text-red-500'
                          }`}>
                            {categoria.tipo === 'ingreso' ? 'Ingreso' : 'Gasto'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => { setCategoriaEditar(categoria); setShowModalCat(true); }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEliminarCategoria(categoria.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

        {/* ── TAB: DATOS ── */}
        {tab === 'datos' && conFinanzas && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full">
                <Database className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900 dark:text-white">Exportar datos</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Descarga todos tus datos en formato JSON
                </p>
              </div>
            </div>

            {/* Resumen de datos */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'Cuentas',                  count: cuentas.length },
                { label: 'Categorías',               count: categorias.length },
                { label: 'Transacciones',             count: transacciones.length },
                { label: 'Traspasos',                 count: traspasos.length },
                { label: 'Presupuestos',              count: presupuestos.length },
                { label: 'Inversiones',               count: inversiones.length },
                { label: 'Aportaciones inversión',    count: aportacionesInversion.length },
                { label: 'Retiros inversión',         count: retirosInversion.length },
                { label: 'Valoraciones inversión',    count: valoracionesInversion.length },
                { label: 'Traspasos recurrentes',     count: traspasosRecurrentes.length },
                { label: 'Transacciones recurrentes', count: transaccionesRecurrentes.length },
              ].map(({ label, count }) => (
                <div key={label} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">{count}</p>
                </div>
              ))}
            </div>

            <button
              onClick={handleExportar}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 transition"
            >
              <Download className="w-5 h-5" />
              Exportar todos los datos (JSON)
            </button>

            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              El archivo se descargará con el nombre <span className="font-mono">gastos-export-{new Date().toISOString().slice(0, 10)}.json</span>
            </p>
          </div>
        )}

      {/* Modales */}
      {showModalCat && (
        <ModalAgregarCategoria
          onClose={() => { setShowModalCat(false); setCategoriaEditar(undefined); }}
          categoriaEditar={categoriaEditar}
        />
      )}
      {showPlantilla && <ModalPlantillaCategorias onClose={() => setShowPlantilla(false)} />}
      {showModalNuevaCuenta && (
        <ModalAgregarCuenta onClose={() => setShowModalNuevaCuenta(false)} />
      )}
      {cuentaEditar && (
        <ModalEditarCuenta
          cuenta={cuentaEditar}
          onClose={() => setCuentaEditar(null)}
        />
      )}
    </div>
  );
};
